import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import {
  GitPullRequest,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { apiClient } from '../../api/client';
import { GitHubPRCreationDialog } from './GitHubPRCreationDialog';

interface GitHubDeployButtonProps {
  yamlContent: string;
  sourceType: string;
  sourceId?: string;
  defaultTitle?: string;
  defaultDescription?: string;
  variant?: 'default' | 'outline' | 'secondary';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
  disabled?: boolean;
  children?: React.ReactNode;
}

export const GitHubDeployButton: React.FC<GitHubDeployButtonProps> = ({
  yamlContent,
  sourceType,
  sourceId,
  defaultTitle,
  defaultDescription,
  variant = 'default',
  size = 'default',
  className = '',
  disabled = false,
  children,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState<{ connected: boolean; loading: boolean; error: string | null }>({
    connected: false,
    loading: true,
    error: null,
  });

  // Check GitHub and GitLab integration status
  useEffect(() => {
    checkIntegrationStatus();
  }, []);

  const checkIntegrationStatus = async () => {
    setIntegrationStatus(prev => ({ ...prev, loading: true, error: null }));

    try {
      let hasConnection = false;

      // Check GitHub
      try {
        const ghStatus = await apiClient.getGitHubIntegrationStatus();
        if (ghStatus.is_connected && ghStatus.has_valid_token) {
          hasConnection = true;
        }
      } catch {
        // GitHub not configured - that's fine
      }

      // Check GitLab
      if (!hasConnection) {
        try {
          const glStatus = await apiClient.getGitLabIntegrationStatus();
          if (glStatus.is_connected) {
            hasConnection = true;
          }
        } catch {
          // GitLab not configured - that's fine
        }
      }

      setIntegrationStatus({
        connected: hasConnection,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      setIntegrationStatus({
        connected: false,
        loading: false,
        error: error.response?.data?.message || 'Failed to check integration status',
      });
    }
  };

  const handleClick = () => {
    if (!integrationStatus.connected) {
      return;
    }

    setIsDialogOpen(true);
  };

  // Don't show button if not connected (unless there's an error to show)
  if (!integrationStatus.loading && !integrationStatus.connected && !integrationStatus.error) {
    return null;
  }

  return (
    <>
      {integrationStatus.error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            CI/CD integration error: {integrationStatus.error}
          </AlertDescription>
        </Alert>
      )}

      <Button
        variant={variant}
        size={size}
        className={className}
        disabled={disabled || integrationStatus.loading || !integrationStatus.connected}
        onClick={handleClick}
      >
        {integrationStatus.loading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <GitPullRequest className="h-4 w-4 mr-2" />
        )}
        {children || 'Deploy to CI/CD'}
      </Button>

      <GitHubPRCreationDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        yamlContent={yamlContent}
        sourceType={sourceType}
        sourceId={sourceId}
        defaultTitle={defaultTitle}
        defaultDescription={defaultDescription}
      />
    </>
  );
};

// Convenience component for quick integration
export const DeployToGitHubButton: React.FC<Omit<GitHubDeployButtonProps, 'variant' | 'size'>> = (props) => (
  <GitHubDeployButton {...props} variant="outline" size="sm" />
);